import { effect, stop } from '@vue/reactivity'
import { evaluateInScope } from '../component/utils'

export default {

    signature: 'qv-bind',

    handle(node, scope) {

        if (node.nodeType !== Node.ELEMENT_NODE) {
            return null
        }

        if (node.hasAttribute('qv-for')) {
            return null
        }

        const cleanupCallbacks = []

        for (const attribute of node.attributes) {

            if (!attribute.name.startsWith(this.signature)) {
                continue
            }

            const [ , attributeName ] = attribute.name.split(':')
            const expression = attribute.value

            const runner = effect(() => {

                const result = evaluateInScope(expression, scope)

                if (attributeName === 'class') {

                    if (!node._qvOriginalClassList) {
                        node._qvOriginalClassList = new Set(node.classList)
                    }

                    const currentClasses = new Set(node.classList)

                    // Remove any dynamic class previously added
                    for (const className of currentClasses) {

                        if (!node._qvOriginalClassList.has(className)) {
                            node.classList.remove(className)
                        }

                    }

                    // Handle object or array
                    if (typeof result === 'object' && !Array.isArray(result)) {

                        // Remove classes for falsy values
                        for (const [ className, value ] of Object.entries(result)) {

                            if (!value) {
                                node.classList.remove(...className.split(' '))
                            }

                        }

                        // Filter truthy values and add all their classes at once
                        const classesToAdd = Object.entries(result)
                            .filter(([ , value ]) => value)
                            .map(([ className ]) => className)
                            .join(' ')

                        // Add all filtered classes
                        if (classesToAdd.length > 0) {
                            node.classList.add(...classesToAdd.split(' '))
                        }

                    } else if (typeof result === 'string' && result.length > 0) {
                        node.classList.add(...result.split(' '))
                    }

                } else if (attributeName === 'style') {

                    if (!node._qvOriginalStyle) {
                        node._qvOriginalStyle = node.style.cssText
                    }

                    node.style.cssText = node._qvOriginalStyle

                    if (typeof result === 'object' && result !== null) {

                        for (const [ property, value ] of Object.entries(result)) {
                            node.style[ property ] = value
                        }

                    }

                } else if (attributeName) {

                    if ([ null, undefined, false ].includes(result) && attributeShouldntBePreservedIfFalsy(attributeName)) {
                        node.removeAttribute(attributeName)
                    } else {

                        if (isBooleanAttribute(attributeName)) {
                            node.setAttribute(attributeName, attributeName)
                        } else {
                            node.setAttribute(attributeName, result)
                        }

                    }

                } else {

                    // Qv-bind="{ id: 'test', href: '...', ... }"
                    if (typeof result === 'object' && result !== null) {

                        for (const [ key, value ] of Object.entries(result)) {
                            node.setAttribute(key, value)
                        }

                    }

                }

            })

            cleanupCallbacks.push(() => {
                stop(runner)
            })

        }

        return () => {

            for (const callback of cleanupCallbacks) {
                callback()
            }

        }

    },

}

// As per HTML spec table https://html.spec.whatwg.org/multipage/indices.html#attributes-3:boolean-attribute
const booleanAttributes = new Set([
    'allowfullscreen',
    'async',
    'autofocus',
    'autoplay',
    'checked',
    'controls',
    'default',
    'defer',
    'disabled',
    'formnovalidate',
    'inert',
    'ismap',
    'itemscope',
    'loop',
    'multiple',
    'muted',
    'nomodule',
    'novalidate',
    'open',
    'playsinline',
    'readonly',
    'required',
    'reversed',
    'selected',
    'shadowrootclonable',
    'shadowrootdelegatesfocus',
    'shadowrootserializable',
])

function isBooleanAttribute(attributeName) {
    return booleanAttributes.has(attributeName)
}

function attributeShouldntBePreservedIfFalsy(attributeName) {
    return ![ 'aria-pressed', 'aria-checked', 'aria-expanded', 'aria-selected' ].includes(attributeName)
}
